import moment from 'moment'

import { SORT_TYPES, PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS, ENROLLMENT_CONNECTION_STATUSES, enrollmentConnectionStatusesOrderLookupTable, KARDIA_PRO_DOB_START_DATE, KARDIA_PRO_DOB_END_DATE } from '../../../constants'
import {
  StandardCompare,
  getName,
  getPendingOrConnectedEnrollmentsByEndDate,
} from '../../../containers/Utilities'
import { comparePatients } from '../../../store/appReducer'

export const sortPatientIds = (
  sorts = {
    sortBy: PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PATIENT,
    sortDirection: SORT_TYPES.ASC,
  },
  pIds = [],
  recByIds = {},
  enrollmentByIds = {},
) => {
  const {
    sortBy,
    sortDirection,
  } = sorts
  const patientIds = [ ...pIds ]
  let sortedPatientIds = patientIds

  if (patientIds) {
    switch (sortBy) {
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PATIENT:
      sortedPatientIds = patientIds.sort((a, b) => comparePatients(recByIds[a], recByIds[b], sortDirection))
      break
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.DOB:
      sortedPatientIds = patientIds.sort((a, b) => StandardCompare.dates(recByIds[a].dob, recByIds[b].dob, sortDirection))
      break
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.ORDERING_PHYSICIAN:
      sortedPatientIds = patientIds.sort((a, b) => {
        const physician1 = recByIds[a].orderingPhysician
          ? getName(recByIds[a].orderingPhysician.firstName, recByIds[a].orderingPhysician.lastName, '')
          : ''
        const physician2 = recByIds[b].orderingPhysician
          ? getName(recByIds[b].orderingPhysician.firstName, recByIds[b].orderingPhysician.lastName, '')
          : ''
        return StandardCompare.strings(physician1, physician2, sortDirection)
      })
      break
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.CONNECTION_STATUS:
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.PRESCRIPTION:
      sortedPatientIds = patientIds.sort((a, b) => {
        const patient1 = recByIds[a]
        const patient2 = recByIds[b]
        const status1 = enrollmentConnectionStatusesOrderLookupTable[patient1.connectionStatus]
        const status2 = enrollmentConnectionStatusesOrderLookupTable[patient2.connectionStatus]

        const result = status1 === status2
        if (result) {
          let enrollment1 = ''
          let enrollment2 = ''

          if (patient1.enrollments && patient1.enrollments.length > 0) {
            const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient1.enrollments, enrollmentByIds)

            if (pendingOrConnectedEnrollments.length > 0) {
              enrollment1 = pendingOrConnectedEnrollments[0].connectionTemplateName
            }
          }

          if (patient2.enrollments && patient2.enrollments.length > 0) {
            const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient2.enrollments, enrollmentByIds)

            if (pendingOrConnectedEnrollments.length > 0) {
              enrollment2 = pendingOrConnectedEnrollments[0].connectionTemplateName
            }
          }

          /* prescription order is the same regardless of conneciton status sort direction */
          return StandardCompare.strings(enrollment1, enrollment2, sortDirection)
        } else {
          if (sortDirection === SORT_TYPES.ASC) {
            return status1 <= status2 ? -1 : 1
          } else {
            return status1 > status2 ? -1 : 1
          }
        }
      })
      break
    case PATIENT_DIRECTORY_TABLE_COLUMN_HEADERS.BILLING_CODE:
      sortedPatientIds = patientIds.sort((a, b) => {
        const patient1 = recByIds[a]
        const patient2 = recByIds[b]

        let enrollment1 = ''
        let enrollment2 = ''
        if (patient1.enrollments && patient1.enrollments.length > 0) {
          const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient1.enrollments, enrollmentByIds)

          if (pendingOrConnectedEnrollments.length > 0) {
            enrollment1 = pendingOrConnectedEnrollments[0]
          }
        }

        if (patient2.enrollments && patient2.enrollments.length > 0) {
          const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient2.enrollments, enrollmentByIds)

          if (pendingOrConnectedEnrollments.length > 0) {
            enrollment2 = pendingOrConnectedEnrollments[0]
          }
        }
        const status1 = enrollmentConnectionStatusesOrderLookupTable[patient1.connectionStatus]
        const status2 = enrollmentConnectionStatusesOrderLookupTable[patient2.connectionStatus]

        const statusResult = status1 === status2
        if (statusResult) {
          const enrollment1Name = enrollment1.connectionTemplateName
          const enrollment2Name = enrollment2.connectionTemplateName
          const enrollmentNameResult = StandardCompare.strings(enrollment1Name, enrollment2Name, sortDirection)
          return enrollmentNameResult
        } else {
          if (sortDirection === SORT_TYPES.ASC) {
            return status1 <= status2 ? -1 : 1
          } else {
            return status1 > status2 ? -1 : 1
          }
        }
      })
      sortedPatientIds = sortedPatientIds.sort((a, b) => {
        const patient1 = recByIds[a]
        const patient2 = recByIds[b]

        let enrollment1 = ''
        let enrollment2 = ''
        let billingCode1 = ''
        let billingCode2 = ''

        if (patient1.enrollments && patient1.enrollments.length > 0) {
          const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient1.enrollments, enrollmentByIds)

          if (pendingOrConnectedEnrollments.length > 0) {
            enrollment1 = pendingOrConnectedEnrollments[0]
          }

          if (enrollment1 && enrollment1.billingCodes && enrollment1.billingCodes.length > 0) {
            billingCode1 = enrollment1.billingCodes[0].name
          }
        }

        if (patient2.enrollments && patient2.enrollments.length > 0) {
          const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient2.enrollments, enrollmentByIds)

          if (pendingOrConnectedEnrollments.length > 0) {
            enrollment2 = pendingOrConnectedEnrollments[0]
          }

          if (enrollment2 && enrollment2.billingCodes && enrollment2.billingCodes.length > 0) {
            billingCode2 = enrollment2.billingCodes[0].name
          }
        }

        const billingCodeResult = StandardCompare.strings(billingCode1, billingCode2, sortDirection)
        return billingCodeResult
      })
      break
    default:
      // No sort, keep previous sort
      break
    }
  }

  return sortedPatientIds
}

export const filterPatientIds = (
  filters = {
    selectedPatientId: undefined,
    selectedStartDate: undefined,
    selectedEndDate: undefined,
    selectedPhysicianId: undefined,
    selectedPhysicians: undefined,
    selectedConnectionStatus: undefined,
    selectedPrescriptionId: undefined,
    selectedBillingCode: undefined,
  },
  patientIds,
  patientByIds,
  enrollmentByIds,
) => {
  const {
    selectedPatientId,
    selectedStartDate,
    selectedEndDate,
    selectedPhysicianId,
    selectedPhysicians,
    selectedConnectionStatus,
    selectedPrescriptionId,
    selectedBillingCode,
  } = filters
  let filteredPatients = patientIds

  if (patientIds) {
    if (selectedPatientId) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const p = patientByIds[patientId]

          return p.id === selectedPatientId
        })
    }

    if (selectedStartDate !== KARDIA_PRO_DOB_START_DATE
      || selectedEndDate !== KARDIA_PRO_DOB_END_DATE
    ) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const p = patientByIds[patientId]

          if (moment(p.dob).isBefore(moment(selectedStartDate).startOf('day'))
            || moment(p.dob).isAfter(moment(selectedEndDate).endOf('day'))) {
            return false
          }

          // if selected dob dates are not default and dob does not exist,
          // remove them from the list
          if (p.dob === null) {
            return false
          }

          return true
        })
    }

    if (selectedPhysicianId) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const p = patientByIds[patientId]

          return p.orderingPhysician && p.orderingPhysician.id === selectedPhysicianId
        })
    }

    if (selectedPhysicians && selectedPhysicians.length > 0) {
      let hasNone = false
      const selectedPhysiciansById = selectedPhysicians
        .filter(p => {
          if (p.id === '0') {
            hasNone = true
          }
          return p.id !== '0'
        })
        .map(p => p.id)

      filteredPatients = filteredPatients
        .filter(patientId => {
          const patient = patientByIds[patientId]

          if (hasNone && !patient.orderingPhysician) {
            return true
          } else if (patient.orderingPhysician) {
            return selectedPhysiciansById.includes(patient.orderingPhysician.id)
          }
          return false
        })
    }

    if (selectedConnectionStatus.length > 0) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const p = patientByIds[patientId]

          return selectedConnectionStatus.some(connectionStatus => {
            switch (connectionStatus.id) {
            case ENROLLMENT_CONNECTION_STATUSES.CONNECTED:
              return StandardCompare.tableStringFilter(p.connectionStatus, connectionStatus.id)
            case ENROLLMENT_CONNECTION_STATUSES.PENDING:
              return StandardCompare.tableStringFilter(p.connectionStatus, connectionStatus.id)
            case ENROLLMENT_CONNECTION_STATUSES.NONE:
              return StandardCompare.tableStringFilter(p.connectionStatus, connectionStatus.id)
                // Different types of disconnection -> none
                || StandardCompare.tableStringFilter(p.connectionStatus, ENROLLMENT_CONNECTION_STATUSES.DISCONNECTED)
                || StandardCompare.tableStringFilter(p.connectionStatus, ENROLLMENT_CONNECTION_STATUSES.EXPIRED)
                || StandardCompare.tableStringFilter(p.connectionStatus, ENROLLMENT_CONNECTION_STATUSES.REVOKED)
            default:
              return false
            }
          })
        })
    }

    if (selectedPrescriptionId) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const p = patientByIds[patientId]

          if (p.enrollments.length === 0) {
            return false
          }

          if (
            !StandardCompare.tableStringFilter(p.connectionStatus, ENROLLMENT_CONNECTION_STATUSES.CONNECTED)
            && !StandardCompare.tableStringFilter(p.connectionStatus, ENROLLMENT_CONNECTION_STATUSES.PENDING)
          ) {
            return false
          }

          let currentEnrollment

          if (p.enrollments && p.enrollments.length > 0 && enrollmentByIds) {
            const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(p.enrollments, enrollmentByIds)

            if (pendingOrConnectedEnrollments.length > 0) {
              currentEnrollment = pendingOrConnectedEnrollments[0]
            }
          }

          return currentEnrollment.connectionTemplateID === selectedPrescriptionId
        })
    }

    if (selectedBillingCode.length > 0) {
      filteredPatients = filteredPatients
        .filter(patientId => {
          const patient = patientByIds[patientId]
          let currentEnrollment
          let id

          if (patient.enrollments && patient.enrollments.length > 0 && enrollmentByIds) {
            const pendingOrConnectedEnrollments = getPendingOrConnectedEnrollmentsByEndDate(patient.enrollments, enrollmentByIds)

            if (pendingOrConnectedEnrollments.length > 0) {
              currentEnrollment = pendingOrConnectedEnrollments[0]
            }
          }

          if (currentEnrollment && currentEnrollment.billingCodes && currentEnrollment.billingCodes.length > 0) {
            ({ id } = currentEnrollment.billingCodes[0])
          }

          return currentEnrollment && selectedBillingCode.some(billingCode => billingCode.id === id)
        })
    }
  }

  return filteredPatients
}



// WEBPACK FOOTER //
// ./src/legacy/containers/Tables/PatientDirectoryTable/Utilities.js